import { create } from 'zustand';
import {
  type UserThemePreference,
  updateUserThemeInSupabase,
} from '@/services/supabase';
import { useUserDataStore } from '@/stores/useUserDataStore';
import {
  clearCustomThemeFromDocument,
  getDefaultDarkTheme,
  isDarkTheme,
  type ThemeId,
} from '@/theme/themes';

export function getSystemThemeId(): ThemeId {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? getDefaultDarkTheme()
    : 'light';
}

export function resolveThemeId(preference: UserThemePreference | undefined): ThemeId {
  if (preference === 'light') return 'light';
  if (preference === 'dark') return getDefaultDarkTheme();
  return getSystemThemeId();
}

function applyThemeToDocument(themeId: ThemeId) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', themeId);
  clearCustomThemeFromDocument();
  if (isDarkTheme(themeId)) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

interface ThemeStore {
  themePreference: UserThemePreference | undefined;
  resolvedThemeId: ThemeId;
  syncPreference: (preference: UserThemePreference | undefined) => void;
  setThemePreference: (preference: UserThemePreference) => void;
}

const initialResolvedThemeId = getSystemThemeId();
applyThemeToDocument(initialResolvedThemeId);

export const useThemeStore = create<ThemeStore>((set, get) => ({
  themePreference: undefined,
  resolvedThemeId: initialResolvedThemeId,

  syncPreference: (preference) => {
    const resolvedThemeId = resolveThemeId(preference);
    applyThemeToDocument(resolvedThemeId);
    set({ themePreference: preference, resolvedThemeId });
  },

  setThemePreference: (preference) => {
    get().syncPreference(preference);
    useUserDataStore.setState((state) => ({
      userData: { ...state.userData, setTheme: preference },
    }));
    void updateUserThemeInSupabase(preference).catch((error) => {
      console.error('Failed to update theme preference:', error);
    });
  },
}));

let mediaQueryListenerAttached = false;

export function initThemeSystemListener() {
  if (mediaQueryListenerAttached || typeof window === 'undefined') return;
  mediaQueryListenerAttached = true;

  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', () => {
    const { themePreference } = useThemeStore.getState();
    if (themePreference === undefined || themePreference === 'default') {
      useThemeStore.getState().syncPreference(themePreference);
    }
  });
}
